import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation, useMatch } from 'react-router';

import { catchAll } from '@sorare/routing';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import {
  ANY_SPORT_MARKET_COMMON_PACKS,
  ANY_SPORT_MARKET_HOME,
  NBA_MARKET_INSTANT_BUY,
  NBA_MARKET_PRIMARY,
  NBA_MARKET_SECONDARY,
  NBA_MARKET_SECONDARY_PLAYERSLUG_RARITY,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useUnclaimedMarketTasksCount } from '@sorare/core/src/hooks/users/useUnclaimedMarketTasksCount';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';
import {
  generateSportPath,
  matchAnySportPath,
} from '@sorare/core/src/lib/routing/generateSportPath';

import { MarketBanners } from '@sorare/marketplace/src/components/MarketBanners';
import { MarketHeader } from '@sorare/marketplace/src/components/MarketHeader';
import { DisabledMarket } from '@sorare/marketplace/src/components/market/DisabledMarket';
import { EnterFromAndroidMarketPlaceTab } from '@sorare/marketplace/src/components/mobileApp/EnterFromAndroidMarketplaceTab';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';

const MarketPage = () => {
  const {
    isIosApp,
    isMarketAuthorizedIosApp,
    isAndroidApp,
    canUseIosPostMessageAndForYou,
  } = useIsMobileApp();
  const { pathname } = useLocation();
  const { filterQuery, filterQueryWithInSeason } =
    useNavigableMarketTabParams();
  const {
    flags: {
      buyOnWebMarketplaceFromAndroid = false,
      useMarketHomePromoBanners = false,
    },
  } = useFeatureFlags();
  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  const matchCommonPacksRoute = useMatch(
    catchAll(
      generateSportPath(ANY_SPORT_MARKET_COMMON_PACKS, { sport: Sport.NBA })
    )
  );
  const isMarketHomeTab =
    useMarketHomePromoBanners &&
    !!matchAnySportPath(ANY_SPORT_MARKET_HOME, pathname);

  const showBanner = !matchCommonPacksRoute && !isMarketHomeTab;

  const disabledMarket =
    isAndroidApp && buyOnWebMarketplaceFromAndroid && !matchCommonPacksRoute;

  return (
    <FullWidthContainer>
      <NoIndex />
      <EnterFromAndroidMarketPlaceTab />
      {!!(!isIosApp || isMarketAuthorizedIosApp) && <MarketHeader />}
      <ErrorBoundary fallback={null}>
        {!!showBanner && <MarketBanners />}
      </ErrorBoundary>
      <ContentWithStickyHeader
        hideHeader={!!isIosApp && !canUseIosPostMessageAndForYou}
        header={
          <FullWidthContainer.NoPadding>
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              <Tab
                to={generateSportPath(ANY_SPORT_MARKET_HOME, {
                  sport: Sport.NBA,
                })}
              >
                <FormattedMessage {...navLabels.tradeMarket} />
              </Tab>
              <Tab to={buildMarketTabRoute(NBA_MARKET_PRIMARY, filterQuery)}>
                <FormattedMessage {...navLabels.auctions} />
              </Tab>
              <Tab
                to={buildMarketTabRoute(NBA_MARKET_INSTANT_BUY, filterQuery)}
              >
                <FormattedMessage {...transferMarket.instantBuy} />
              </Tab>
              {!!(!isIosApp || isMarketAuthorizedIosApp) && (
                <Tab
                  to={buildMarketTabRoute(
                    NBA_MARKET_SECONDARY,
                    filterQueryWithInSeason
                  )}
                  matches={[
                    NBA_MARKET_SECONDARY,
                    NBA_MARKET_SECONDARY_PLAYERSLUG_RARITY,
                  ]}
                >
                  <FormattedMessage {...navLabels.purchases} />
                </Tab>
              )}
              <Tab
                to={generateSportPath(ANY_SPORT_MARKET_COMMON_PACKS, {
                  sport: Sport.NBA,
                })}
              >
                <Badge badgeContent={unclaimedMarketTasksCount}>
                  <FormattedMessage {...navLabels.commonPacks} />
                </Badge>
              </Tab>
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        <Suspense fallback={<LoadingIndicator fullHeight />}>
          <CurrentMarketplaceUserProvider sport={Sport.NBA}>
            {disabledMarket ? <DisabledMarket /> : <Outlet />}
          </CurrentMarketplaceUserProvider>
        </Suspense>
      </ContentWithStickyHeader>
    </FullWidthContainer>
  );
};

export default MarketPage;
